import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  Clock,
  CheckCircle2,
  FileText,
  Gauge,
  Image as ImageIcon,
  Play,
  Search,
  Sparkles,
  Upload,
} from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
  useToast,
} from '@solara/ui-shared';
import {
  searchPeppolDirectory,
  searchUnspsc,
  type PeppolParticipant,
  type UnspscEntry,
} from '@solara/sample-data';
// TODO: replace MOCK_PEPPOL_DIR / MOCK_UNSPSC with backend-backed lookups once exposed.
import { MOCK_PEPPOL_DIR } from '../data/mockData';
import { usePeppolLookup } from '@solara/api-client/react';

const COUNTRY_FLAG: Record<PeppolParticipant['country'], string> = {
  AE: '\u{1F1E6}\u{1F1EA}',
  SA: '\u{1F1F8}\u{1F1E6}',
  OM: '\u{1F1F4}\u{1F1F2}',
  BH: '\u{1F1E7}\u{1F1ED}',
  QA: '\u{1F1F6}\u{1F1E6}',
  KW: '\u{1F1F0}\u{1F1FC}',
};

const SAMPLE_UBL_XML = `<?xml version="1.0" encoding="UTF-8"?>
<Invoice xmlns="urn:oasis:names:specification:ubl:schema:xsd:Invoice-2"
         xmlns:cbc="urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2"
         xmlns:cac="urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2">
  <cbc:CustomizationID>urn:peppol:pint:billing-1@ae-1</cbc:CustomizationID>
  <cbc:ProfileID>urn:peppol:bis:billing</cbc:ProfileID>
  <cbc:ID>INV-2026-04-1042</cbc:ID>
  <cbc:IssueDate>2026-04-30</cbc:IssueDate>
  <cbc:DocumentCurrencyCode>AED</cbc:DocumentCurrencyCode>
  <cac:AccountingSupplierParty>
    <cac:Party>
      <cac:PartyTaxScheme>
        <cbc:CompanyID>100123456700003</cbc:CompanyID>
      </cac:PartyTaxScheme>
    </cac:Party>
  </cac:AccountingSupplierParty>
  <cac:LegalMonetaryTotal>
    <cbc:PayableAmount currencyID="AED">10500.00</cbc:PayableAmount>
  </cac:LegalMonetaryTotal>
</Invoice>`;

interface OcrPreview {
  fileName: string;
  status: 'extracted' | 'pending';
  trn: string;
  totalAed: number;
  vatAed: number;
  lineCount: number;
  confidence: number; // 0–100
}

const OCR_PREVIEWS: OcrPreview[] = [
  {
    fileName: 'al-futtaim-courier-INV-9921.pdf',
    status: 'extracted',
    trn: '100123456700003',
    totalAed: 4_320,
    vatAed: 205.71,
    lineCount: 6,
    confidence: 96,
  },
  {
    fileName: 'sharaf-dg-receipt-2026-04-19.jpg',
    status: 'extracted',
    trn: '100876543200001',
    totalAed: 11_780,
    vatAed: 561.43,
    lineCount: 4,
    confidence: 91,
  },
  {
    fileName: 'spinneys-monthly-2026-04.pdf',
    status: 'extracted',
    trn: '100445566700009',
    totalAed: 2_145,
    vatAed: 102.14,
    lineCount: 9,
    confidence: 88,
  },
  {
    fileName: 'tristar-engineering-INV-3308.pdf',
    status: 'pending',
    trn: '—',
    totalAed: 0,
    vatAed: 0,
    lineCount: 0,
    confidence: 0,
  },
];

export default function ToolsPage() {
  const { t } = useTranslation('tickstar');
  const { t: tP1 } = useTranslation('p1');
  const { locale } = useI18n();
  const { toast } = useToast();

  const numberFmt = useMemo(() => new Intl.NumberFormat(locale), [locale]);
  const aedFmt = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'AED',
        maximumFractionDigits: 0,
      }),
    [locale],
  );
  const aedDecimalFmt = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'AED',
        maximumFractionDigits: 2,
      }),
    [locale],
  );
  const dateTimeFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }),
    [locale],
  );

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('tools.title')}</h1>
        <p className="text-fg-muted">{t('tools.subtitle')}</p>
      </header>

      <Tabs defaultValue="unspsc">
        <TabsList>
          <TabsTrigger value="unspsc">{tP1('tools.tabUnspsc')}</TabsTrigger>
          <TabsTrigger value="peppol">{tP1('tools.tabPeppol')}</TabsTrigger>
          <TabsTrigger value="validate">{tP1('tools.tabValidate')}</TabsTrigger>
          <TabsTrigger value="ocr">{tP1('tools.tabOcr')}</TabsTrigger>
        </TabsList>

        <TabsContent value="unspsc">
          <UnspscTool t={t} locale={locale} numberFmt={numberFmt} />
        </TabsContent>

        <TabsContent value="peppol">
          <PeppolTool t={t} locale={locale} />
        </TabsContent>

        <TabsContent value="validate">
          <ValidateTool
            t={t}
            toast={toast}
            dateTimeFmt={dateTimeFmt}
          />
        </TabsContent>

        <TabsContent value="ocr">
          <OcrTool t={t} aedFmt={aedFmt} aedDecimalFmt={aedDecimalFmt} numberFmt={numberFmt} />
        </TabsContent>
      </Tabs>

    </div>
  );
}

/* ──────────────────────────── UNSPSC ──────────────────────────── */

function UnspscTool({
  t,
  locale,
  numberFmt,
}: {
  t: (k: string, vars?: Record<string, string | number>) => string;
  locale: 'en-AE' | 'ar-AE' | 'hu-HU';
  numberFmt: Intl.NumberFormat;
}) {
  const { t: tP1 } = useTranslation('p1');
  const [query, setQuery] = useState('');
  const results: UnspscEntry[] = useMemo(
    () => searchUnspsc(query, locale),
    [query, locale],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-brand-600" />
          {t('tools.unspscTitle')}
        </CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        <div className="relative max-w-xl">
          <Search className="absolute inset-y-0 start-3 my-auto h-4 w-4 text-fg-subtle" />
          <input
            type="search"
            placeholder={t('tools.unspscPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full h-10 ps-9 pe-3 rounded-md bg-surface border border-border text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
          />
        </div>

        {results.length === 0 ? (
          <EmptyState
            icon={<Search className="h-6 w-6" />}
            title="No UNSPSC matches."
            description="Try a different code, family or commodity name."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-fg-subtle">
                  <th className="text-start font-medium pb-2">{tP1('tools.code')}</th>
                  <th className="text-start font-medium pb-2">{tP1('tools.title')}</th>
                  <th className="text-start font-medium pb-2">{tP1('tools.family')}</th>
                  <th className="text-start font-medium pb-2">VAT default</th>
                  <th className="text-start font-medium pb-2">AI confidence</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {results.map((u) => {
                  const title = locale === 'ar-AE' ? u.titleAr : u.titleEn;
                  return (
                    <tr key={`${u.code}-${u.titleEn}`}>
                      <td className="py-2 num text-xs tabular-nums">
                        <code>{u.code}</code>
                      </td>
                      <td className="py-2">
                        <div className="font-medium text-fg">
                          <bdi dir="auto">{title}</bdi>
                        </div>
                        <div className="text-[10px] text-fg-subtle">
                          <bdi dir="auto">{u.segment}</bdi>
                        </div>
                      </td>
                      <td className="py-2 text-xs text-fg-muted">
                        <bdi dir="auto">{u.family}</bdi>
                      </td>
                      <td className="py-2">
                        <Badge tone={vatBadgeTone(u.vatDefault)}>
                          {vatLabel(u.vatDefault)}
                        </Badge>
                      </td>
                      <td className="py-2">
                        <Badge tone={confidenceBadgeTone(u.aiConfidence)}>
                          {u.aiConfidence}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="text-[11px] text-fg-subtle mt-3">
              {numberFmt.format(results.length)} matching code{results.length === 1 ? '' : 's'}.
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function vatBadgeTone(v: UnspscEntry['vatDefault']): 'success' | 'info' | 'warning' {
  if (v === 'STANDARD') return 'info';
  if (v === 'ZERO') return 'success';
  return 'warning';
}

function vatLabel(v: UnspscEntry['vatDefault']): string {
  if (v === 'STANDARD') return 'Standard 5%';
  if (v === 'ZERO') return 'Zero rated';
  return 'Exempt';
}

function confidenceBadgeTone(c: 'high' | 'medium' | 'low'): 'success' | 'warning' | 'neutral' {
  if (c === 'high') return 'success';
  if (c === 'medium') return 'warning';
  return 'neutral';
}

/* ──────────────────────────── Peppol ──────────────────────────── */

function PeppolTool({
  t,
  locale,
}: {
  t: (k: string, vars?: Record<string, string | number>) => string;
  locale: 'en-AE' | 'ar-AE' | 'hu-HU';
}) {
  const [query, setQuery] = useState('');
  // Live lookup against the Solara Core SMP — fires when the operator
  // submits a full participantId (iso6523-actorid-upis::*).
  const lookup = usePeppolLookup();
  const results: PeppolParticipant[] = useMemo(
    () => searchPeppolDirectory(MOCK_PEPPOL_DIR, query, locale),
    [query, locale],
  );
  void lookup;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="h-5 w-5 text-brand-600" />
          {t('tools.peppolTitle')}
        </CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        <div className="relative max-w-xl">
          <Search className="absolute inset-y-0 start-3 my-auto h-4 w-4 text-fg-subtle" />
          <input
            type="search"
            placeholder={t('tools.peppolPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full h-10 ps-9 pe-3 rounded-md bg-surface border border-border text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
          />
        </div>

        {results.length === 0 ? (
          <EmptyState
            icon={<Search className="h-6 w-6" />}
            title="No participants found."
            description="Search by participant ID, legal name, country code, or service provider."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-fg-subtle">
                  <th className="text-start font-medium pb-2">Participant ID</th>
                  <th className="text-start font-medium pb-2">Legal name</th>
                  <th className="text-start font-medium pb-2">Country</th>
                  <th className="text-start font-medium pb-2">Service provider</th>
                  <th className="text-start font-medium pb-2">Capabilities</th>
                  <th className="text-start font-medium pb-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {results.map((p) => {
                  const isJv = p.serviceProvider === 'Solara Platform (UAE)';
                  const name = locale === 'ar-AE' ? p.legalNameAr : p.legalName;
                  return (
                    <tr
                      key={p.participantId}
                      className={cn(
                        isJv && 'border-s-2 border-s-amber-500 bg-amber-50/30',
                      )}
                    >
                      <td className="py-2 ps-3 text-xs">
                        <code>{p.participantId}</code>
                      </td>
                      <td className="py-2">
                        <div className="font-medium text-fg">
                          <bdi dir="auto">{name}</bdi>
                        </div>
                      </td>
                      <td className="py-2 text-xs">
                        <span className="me-1">{COUNTRY_FLAG[p.country]}</span>
                        {p.country}
                      </td>
                      <td className="py-2 text-xs">
                        {isJv ? (
                          <span className="inline-flex items-center gap-1 text-amber-700 font-medium">
                            {p.serviceProvider}
                          </span>
                        ) : (
                          <span className="text-fg-muted">{p.serviceProvider}</span>
                        )}
                      </td>
                      <td className="py-2">
                        <div className="flex flex-wrap gap-1">
                          {p.capabilities.map((c) => (
                            <span
                              key={c}
                              className="inline-flex items-center rounded-full bg-surface-muted text-fg-muted border border-border px-2 py-0.5 text-[10px] font-medium"
                            >
                              {c}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-2">
                        <Badge
                          tone={
                            p.status === 'active'
                              ? 'success'
                              : p.status === 'pending-onboarding'
                              ? 'warning'
                              : 'neutral'
                          }
                        >
                          {p.status}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/* ──────────────────────────── Validate ──────────────────────────── */

interface ValidateRunResult {
  ok: boolean;
  ranAt: number;
  durationMs: number;
  ruleCount: number;
  failedRule?: { code: string; message: string };
}

function ValidateTool({
  t,
  toast,
  dateTimeFmt,
}: {
  t: (k: string, vars?: Record<string, string | number>) => string;
  toast: ReturnType<typeof useToast>['toast'];
  dateTimeFmt: Intl.DateTimeFormat;
}) {
  const [xml, setXml] = useState(SAMPLE_UBL_XML);
  const [result, setResult] = useState<ValidateRunResult | null>(null);

  function runValidate() {
    const fail = Math.random() < 0.25;
    const ranAt = Date.now();
    const next: ValidateRunResult = fail
      ? {
          ok: false,
          ranAt,
          durationMs: 412,
          ruleCount: 187,
          failedRule: {
            code: 'BR-AE-VAT-08',
            message:
              'Sum of line VAT amounts must equal the document-level VAT total to two decimal places.',
          },
        }
      : { ok: true, ranAt, durationMs: 318, ruleCount: 187 };

    setResult(next);
    if (next.ok) {
      toast({
        title: 'Schematron pass — 0 rules failed.',
        description: 'PINT-AE 1.0.3 sandbox · 187 rules evaluated.',
        tone: 'success',
      });
    } else {
      toast({
        title: `${next.failedRule!.code} failed.`,
        description: next.failedRule!.message,
        tone: 'danger',
      });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gauge className="h-5 w-5 text-brand-600" />
          {t('tools.validateTitle')}
        </CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="text-sm text-fg-muted">{t('tools.validateBody')}</p>

        <div className="grid gap-4 lg:grid-cols-2">
          {/* Editor */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-fg-muted uppercase tracking-wide">
              UBL XML
            </label>
            <textarea
              value={xml}
              onChange={(e) => setXml(e.target.value)}
              spellCheck={false}
              dir="ltr"
              className="w-full h-72 rounded-md border border-border bg-surface text-fg font-mono text-xs p-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
            />
            <Button onClick={runValidate} className="gap-1.5">
              <Play className="h-3.5 w-3.5 rtl-mirror" />
              Run validate
            </Button>
          </div>

          {/* Results */}
          <div className="space-y-3">
            <label className="text-xs font-medium text-fg-muted uppercase tracking-wide">
              Results
            </label>
            {result ? (
              <div
                className={cn(
                  'rounded-xl border p-4 space-y-3',
                  result.ok
                    ? 'border-success/40 bg-emerald-50/60'
                    : 'border-danger/40 bg-red-50/60',
                )}
              >
                <div className="flex items-center gap-2">
                  {result.ok ? (
                    <CheckCircle2 className="h-5 w-5 text-success" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-danger" />
                  )}
                  <div className="font-medium">
                    {result.ok ? 'Schematron pass' : 'Schematron fail'}
                  </div>
                  <Badge tone={result.ok ? 'success' : 'danger'} className="ms-auto">
                    {result.ok ? 'PASS' : 'FAIL'}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <Stat label="Rules" value={String(result.ruleCount)} />
                  <Stat label="Duration" value={`${result.durationMs} ms`} />
                  <Stat
                    label="Last run"
                    value={dateTimeFmt.format(new Date(result.ranAt))}
                  />
                  <Stat label="Endpoint" value="tickstar-sandbox" />
                </div>
                {result.failedRule ? (
                  <div className="rounded-md border border-danger/40 bg-surface p-3 text-xs space-y-1">
                    <div className="font-mono text-danger">
                      {result.failedRule.code}
                    </div>
                    <div className="text-fg-muted">
                      <bdi dir="auto">{result.failedRule.message}</bdi>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-fg-muted">
                    All {result.ruleCount} PINT-AE rules satisfied.
                  </div>
                )}
              </div>
            ) : (
              <EmptyState
                icon={<Play className="h-6 w-6 rtl-mirror" />}
                title="No validation runs yet."
                description="Click Run validate to round-trip the XML through the TickStar sandbox."
              />
            )}

            <div className="flex items-center gap-2 text-[11px] text-fg-subtle">
              <Clock className="h-3 w-3" />
              <span>Sandbox endpoint · throws BR-AE-VAT-08 ~25% of the time for demo realism.</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-100 px-2 py-0.5 text-[10px] font-medium">{t('brand.partner')}</span>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-0.5">
      <div className="text-[10px] uppercase tracking-wide text-fg-subtle">{label}</div>
      <div className="font-medium text-fg tabular-nums">{value}</div>
    </div>
  );
}

/* ──────────────────────────── OCR ──────────────────────────── */

function OcrTool({
  t,
  aedFmt,
  aedDecimalFmt,
  numberFmt,
}: {
  t: (k: string, vars?: Record<string, string | number>) => string;
  aedFmt: Intl.NumberFormat;
  aedDecimalFmt: Intl.NumberFormat;
  numberFmt: Intl.NumberFormat;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ImageIcon className="h-5 w-5 text-brand-600" />
          {t('tools.ocrTitle')}
        </CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="text-sm text-fg-muted">{t('tools.ocrBody')}</p>

        {/* Drop zone (visual only) */}
        <div
          className={cn(
            'rounded-xl border-2 border-dashed border-border-strong bg-surface-muted/40',
            'p-8 text-center text-fg-subtle',
            'flex flex-col items-center gap-3',
          )}
        >
          <div className="h-12 w-12 rounded-full bg-surface grid place-items-center text-brand-600">
            <Upload className="h-6 w-6" />
          </div>
          <div className="text-sm font-medium text-fg">{t('tools.ocrUploadCta')}</div>
          <div className="text-xs">PDF, JPG or PNG up to 25 MB · idempotent intake</div>
          <Button variant="secondary" size="sm" className="mt-1">
            Browse files
          </Button>
        </div>

        {/* Preview grid */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {OCR_PREVIEWS.map((file) => (
            <OcrCard
              key={file.fileName}
              file={file}
              aedFmt={aedFmt}
              aedDecimalFmt={aedDecimalFmt}
              numberFmt={numberFmt}
            />
          ))}
        </div>
      </CardBody>
    </Card>
  );
}

function OcrCard({
  file,
  aedFmt,
  aedDecimalFmt,
  numberFmt,
}: {
  file: OcrPreview;
  aedFmt: Intl.NumberFormat;
  aedDecimalFmt: Intl.NumberFormat;
  numberFmt: Intl.NumberFormat;
}) {
  const isPending = file.status === 'pending';
  return (
    <div className="rounded-xl border border-border bg-surface overflow-hidden">
      {/* Thumbnail placeholder gradient */}
      <div
        className={cn(
          'h-24 bg-gradient-to-br',
          isPending
            ? 'from-surface-muted to-surface'
            : 'from-brand-100 to-amber-50',
          'flex items-center justify-center',
        )}
      >
        <FileText
          className={cn(
            'h-8 w-8',
            isPending ? 'text-fg-subtle' : 'text-brand-700',
          )}
        />
      </div>
      <div className="p-3 space-y-2">
        <div className="text-xs font-medium text-fg truncate" title={file.fileName}>
          {file.fileName}
        </div>
        {isPending ? (
          <div className="flex items-center gap-2 text-xs text-fg-muted">
            <Clock className="h-3.5 w-3.5" />
            <span>Awaiting OCR…</span>
          </div>
        ) : (
          <>
            <dl className="text-[11px] space-y-1">
              <Field label="TRN" value={file.trn} mono />
              <Field label="Total" value={aedFmt.format(file.totalAed)} />
              <Field label="VAT" value={aedDecimalFmt.format(file.vatAed)} />
              <Field
                label="Lines"
                value={numberFmt.format(file.lineCount)}
              />
            </dl>
            <div className="flex items-center justify-between pt-1">
              <span className="text-[10px] uppercase tracking-wide text-fg-subtle">
                Confidence
              </span>
              <Badge
                tone={
                  file.confidence >= 95
                    ? 'success'
                    : file.confidence >= 85
                    ? 'info'
                    : 'warning'
                }
              >
                {file.confidence}%
              </Badge>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-fg-subtle">{label}</dt>
      <dd
        className={cn(
          'tabular-nums text-end',
          mono ? 'font-mono text-[10px]' : 'text-fg',
        )}
      >
        {value}
      </dd>
    </div>
  );
}
